import { useState, useEffect, useRef, useCallback } from 'react';
import { getExtractionFieldsByRulesetType } from '../services/api/rulesetApi';
import { 
  createExtractionField, 
  updateExtractionField, 
  deleteExtractionField,
  bulkCopyFields
} from '../services/api/extractionApi';

/**
 * Custom hook for managing field configurations for a specific rule set
 * @param {string} customerId - The ID of the customer
 * @param {string} rulesetId - The ID of the rule set
 * @returns {Object} Field configuration state and CRUD operations
 */
export const useFieldConfiguration = (customerId, rulesetId) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use ref to prevent duplicate API calls in StrictMode
  const hasFetched = useRef(false);
  const currentCustomerId = useRef(customerId);
  const currentRulesetId = useRef(rulesetId);

  useEffect(() => {
    // Reset when IDs change
    if (currentCustomerId.current !== customerId || currentRulesetId.current !== rulesetId) {
      currentCustomerId.current = customerId;
      currentRulesetId.current = rulesetId;
      hasFetched.current = false;
      setFields([]);
    }

    if (!hasFetched.current && customerId && rulesetId) {
      hasFetched.current = true;
      fetchFields();
    }
  }, [customerId, rulesetId]);

  const fetchFields = async () => {
    if (!customerId || !rulesetId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getExtractionFieldsByRulesetType(rulesetId);
      setFields(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch field configurations');
      console.error('Error fetching fields:', err);
    } finally {
      setLoading(false);
    }
  };

  const createField = async (fieldData) => {
    try {
      setError(null);

      // Validate field key uniqueness within rule set
      const existingField = fields.find(f => f.fieldKey === fieldData.fieldKey);
      if (existingField) {
        throw new Error('A field with this key already exists in this rule set');
      }

      // Automatically associate with rule set ID
      const dataWithRuleset = {
        ...fieldData,
        extractionRulesetTypeId: rulesetId
      };

      const response = await createExtractionField(dataWithRuleset);
      
      // Refresh the list
      await fetchFields();
      
      return response.data;
    } catch (err) {
      // Preserve the full error response for validation errors
      if (err.response?.data?.errors) {
        const error = new Error(err.response.data.message || 'Validation failed');
        error.response = err.response;
        throw error;
      }
      
      const errorMessage = err.message || err.response?.data?.message || 'Failed to create field';
      const error = new Error(errorMessage);
      error.response = err.response;
      setError(errorMessage);
      console.error('Error creating field:', err);
      throw error;
    }
  };

  const updateField = async (id, fieldData) => {
    try {
      setError(null);

      // Validate field key uniqueness (excluding current field)
      if (fieldData.fieldKey) {
        const existingField = fields.find(f => f.fieldKey === fieldData.fieldKey && f.id !== id);
        if (existingField) {
          throw new Error('A field with this key already exists in this rule set');
        }
      }

      const response = await updateExtractionField(id, {
        ...fieldData,
        extractionRulesetTypeId: parseInt(rulesetId)
      });
      
      // Refresh the list
      await fetchFields();
      
      return response.data;
    } catch (err) {
      // Preserve the full error response for validation errors
      if (err.response?.data?.errors) {
        const error = new Error(err.response.data.message || 'Validation failed');
        error.response = err.response;
        throw error;
      }
      
      const errorMessage = err.message || err.response?.data?.message || 'Failed to update field';
      const error = new Error(errorMessage);
      error.response = err.response;
      setError(errorMessage);
      console.error('Error updating field:', err);
      throw error;
    }
  };

  const deleteField = async (id) => {
    try {
      setError(null);

      await deleteExtractionField(id);
      
      // Refresh the list
      await fetchFields();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete field');
      console.error('Error deleting field:', err);
      throw err;
    }
  };

  const importFields = async (file) => {
    try {
      setError(null);

      // Read and parse the JSON file
      const text = await file.text();
      const importedFields = JSON.parse(text);

      if (!Array.isArray(importedFields)) {
        throw new Error('Invalid import file format. Expected an array of fields.');
      }

      // Import each field
      let importedCount = 0;
      for (const fieldData of importedFields) {
        try {
          // Check if field already exists by fieldKey
          const existingField = fields.find(f => f.fieldKey === fieldData.fieldKey);
          
          if (existingField) {
            // Update existing field — strip id so we use the existing one
            const { id: _ignoreId, ...updateData } = fieldData;
            await updateExtractionField(existingField.id, {
              ...updateData,
              extractionRulesetTypeId: rulesetId
            });
          } else {
            // Create new field — strip id so the backend generates a fresh one
            const { id: _ignoreId, ...createData } = fieldData;
            await createExtractionField({
              ...createData,
              id: null,
              extractionRulesetTypeId: rulesetId
            });
          }
          importedCount++;
        } catch (fieldError) {
          console.error(`Error importing field ${fieldData.fieldKey}:`, fieldError);
        }
      }

      // Refresh the list
      await fetchFields();
      
      return { importedCount, totalCount: importedFields.length };
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to import fields';
      setError(errorMessage);
      console.error('Error importing fields:', err);
      throw err;
    }
  };

  const exportFields = () => {
    try {
      // Create JSON blob
      const json = JSON.stringify(fields, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fields-ruleset-${rulesetId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export fields');
      console.error('Error exporting fields:', err);
      throw err;
    }
  };

  const copyFromRuleset = async (sourceRulesetId, fieldIds) => {
    try {
      setError(null);

      // Use the bulk copy API endpoint
      const response = await bulkCopyFields(sourceRulesetId, rulesetId, fieldIds);
      const result = response.data;
      
      console.log('Bulk copy completed:', {
        copiedCount: result.copiedCount,
        skippedCount: result.skippedCount,
        totalCount: result.totalCount
      });
      
      // Refresh the list
      await fetchFields();
      
      return { 
        copiedCount: result.copiedCount, 
        totalCount: result.totalCount,
        skippedCount: result.skippedCount,
        skippedFieldKeys: result.skippedFieldKeys || [],
        errors: result.errors || []
      };
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to copy fields';
      setError(errorMessage);
      console.error('Error copying fields:', err);
      throw err;
    }
  };

  const refreshFields = useCallback(async () => {
    hasFetched.current = false;
    await fetchFields();
  }, [customerId, rulesetId]);

  return {
    fields,
    loading,
    error,
    createField,
    updateField,
    deleteField,
    importFields,
    exportFields,
    copyFromRuleset,
    refreshFields
  };
};
